import React from 'react';

import { cssMap } from '@atlaskit/css';
import FileIcon from '@atlaskit/icon/core/file';
import PageIcon from '@atlaskit/icon/core/page';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import VideoIcon from '@atlaskit/icon/core/video';
import Image from '@atlaskit/image';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { useFetchSmartLinkData } from '../../../common/controller/use-fetch-smartlink-data/useFetchSmartLinkData';
import type { SmartLinkElement } from '../../dynamic-layout-parser/types/dynamic-layout-section';

interface SmartLinkComponentProps {
	component: SmartLinkElement;
}

const useEmbedInlineCardUrlPrefix = ['https://www.loom.com/', 'https://docs.google.com/'];

const styles = cssMap({
	anchor: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
		},
	},
	container: {
		width: '295px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
			cursor: 'pointer',
		},
	},
	embedContainer: {
		width: '388px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
			cursor: 'pointer',
		},
		// @ts-ignore
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-testid="embed-card-resolved-view"] ': {
			border: `1px solid ${token('color.border')}`,
			borderRadius: token('radius.large'),
			width: '388px',
			height: '219px',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
			gap: token('space.025'),
			boxSizing: 'border-box',
			// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& .embed-header': {
				opacity: 1,
			},
			// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'&::after': {
				display: 'none',
			},
			// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& iframe': {
				// width: '368px',
				// height: '188px',				//hack to make google slide with a smaller bottom bar size
				transform: 'scale(0.5)',
				transformOrigin: 'top left',
				width: '200%',
				height: '200%',
				overflow: 'hidden',
			},
		},
	},
	content: {
		width: '295px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	titleBox: {
		maxWidth: '220px',
	},
	summaryBox: {
		marginTop: token('space.200'),
		maxWidth: '340px',
	},
	pageIconWrapper: {
		height: '32px',
		width: '32px',
		boxSizing: 'border-box',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
	},
	documentMetadataIconWrapper: {
		height: '16px',
		width: '16px',
	},
});

const getDocumentIcon = (itemData: any) => {
	if (itemData.url && itemData.url.includes('docs.google.com')) {
		return <FileIcon color={token('color.icon.success')} label="" />;
	}
	if (itemData.url && itemData.url.includes('loom.com')) {
		return <VideoIcon color={token('color.icon.discovery')} label="" />;
	}
	if (
		itemData.url &&
		(itemData.url.includes('bitbucket.org') || itemData.url.includes('github.com'))
	) {
		return <PullRequestIcon color={token('color.icon.accent.green')} label="" />;
	}
	return <PageIcon color={token('color.icon.brand')} label="" />;
};

const EmbedInlineCard = ({ url }: { url: string }) => {
	return (
		<Box xcss={styles.embedContainer}>
			<Card appearance="embed" url={url} actionOptions={{ hide: false }} inheritDimensions={true} />
		</Box>
	);
};

export const DynamicLayoutSmartLinkInternal = ({
	component,
}: SmartLinkComponentProps): React.JSX.Element | null => {
	const url = component.attributes?.url || '';
	const { objectResolverData } = useFetchSmartLinkData({ urls: [url] });
	const resolvedData = objectResolverData?.[0];
	const userMetadata = resolvedData?.['atlassian:ownedBy'] || resolvedData?.attributedTo;

	return resolvedData ? (
		<Anchor
			href={url}
			xcss={styles.anchor}
			aria-label=""
			tabIndex={-1}
			testId="dynamic-layout-smart-link"
		>
			<Box xcss={styles.container}>
				<Box xcss={styles.content}>
					<Inline space="space.150" alignBlock="center">
						<Box xcss={styles.pageIconWrapper}>{getDocumentIcon(resolvedData)}</Box>
						<Stack space="space.050">
							<Box xcss={styles.titleBox}>
								<Text maxLines={1} testId="smart-link-title">
									{resolvedData.name}
								</Text>
							</Box>
							{resolvedData.generator && (
								<Inline space="space.050">
									<Box xcss={styles.documentMetadataIconWrapper}>
										<Image src={resolvedData.generator.icon?.url} alt="" />
									</Box>
									<Text color="color.text.subtle" size="small" maxLines={1}>
										{resolvedData.generator.name}
									</Text>
									<Text color="color.text.subtle" size="small">
										{userMetadata?.name ? `• ${userMetadata.name}` : ''}
									</Text>
								</Inline>
							)}
						</Stack>
					</Inline>
					<Box xcss={styles.summaryBox}>
						<Stack space="space.050">
							<Text color="color.text.subtle" size="small" maxLines={3}>
								{resolvedData.summary}
							</Text>
						</Stack>
					</Box>
				</Box>
			</Box>
		</Anchor>
	) : null;
};

export const DynamicLayoutSmartLink = ({
	component,
}: SmartLinkComponentProps): React.JSX.Element => {
	const url = component.attributes?.url || '';

	if (useEmbedInlineCardUrlPrefix.some((prefix) => url.startsWith(prefix))) {
		return <EmbedInlineCard url={url} />;
	}

	return <DynamicLayoutSmartLinkInternal component={component} />;
};
